import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { SiteContextService, SiteSummary } from '../core/site-context.service';
import { IdentityService } from '../core/identity/identity.service';
import { AgentMentionOption } from './agent-mention.model';

// @-mention source for the sites a user can access ("compare @a with @b"). Intersected with the
// user's own JWT site ids so admins (whose account list spans the whole platform) still see only
// their own sites. Visibility scoping only; the Agents backend re-validates every id.
@Injectable({ providedIn: 'root' })
export class AgentSiteSource {
  private readonly siteContext = inject(SiteContextService);
  private readonly identity = inject(IdentityService);
  private readonly translate = inject(TranslateService);

  private cache: AgentMentionOption[] | null = null;
  private inFlight: Promise<AgentMentionOption[]> | null = null;

  sites(): Promise<AgentMentionOption[]> {
    if (this.cache) {
      return Promise.resolve(this.cache);
    }
    if (this.inFlight) {
      return this.inFlight;
    }
    this.inFlight = this.load();
    return this.inFlight;
  }

  private async load(): Promise<AgentMentionOption[]> {
    try {
      const accessible = new Set(this.identity.currentIdentity?.getAllSiteIds() ?? []);
      const sites = await this.siteContext.getAllSitesAsync();
      const kindLabel = this.translate.instant('agent.inspect.site');
      this.cache = sites
        .filter((site) => accessible.has(site.id))
        .map((site) => toOption(site, kindLabel))
        .sort((a, b) => a.label.localeCompare(b.label));
      return this.cache;
    } catch {
      this.cache = null;
      return [];
    } finally {
      this.inFlight = null;
    }
  }
}

function toOption(site: SiteSummary, kindLabel: string): AgentMentionOption {
  const label = (site.name ?? `#${site.id}`).trim();
  return { kind: 'site', id: site.id, label, searchText: label.toLowerCase(), hint: `${kindLabel} · #${site.id}` };
}
